import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import EmailComposer from "../../../../../components/EmailComposer";

const defaultQuickActions = {
  bccMe: false,
  bccOrderRep: false,
  bccClientRep: false,
  requestReadReceipt: false,
  attachPdf: false,
  attachDashboard: false,
};

const asContact = (email, name) => {
  const normalized = String(email || "").trim().toLowerCase();
  if (!normalized) return null;
  return { id: normalized, email: normalized, name: name || normalized };
};

const documentTemplateTerms = {
  presentation: ["presentation"],
  estimate: ["estimate", "estimation"],
  agreement: ["agreement", "sales order", "sales-order", "sales_order"],
  salesOrder: ["agreement", "sales order", "sales-order", "sales_order"],
};

const crmTemplateCategories = new Set([
  "CRM Follow-up",
  "CRM Estimate Follow-up",
  "CRM Presentation Follow-up",
  "CRM Stale Opportunity",
  "CRM Re-engagement",
  "CRM Lost Opportunity Reopen",
  "CRM Meeting Reminder",
  "CRM Internal Rep Reminder",
]);

const templateText = (template = {}) =>
  [
    template.key,
    template.name,
    template.category,
    template.type,
    template.templateType,
    template.documentType,
    template.defaultFor,
    template.defaultDocumentType,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const isDefaultTemplate = (template = {}) =>
  template.isDefault === true ||
  template.default === true ||
  template.defaultTemplate === true ||
  template.isDefaultTemplate === true;

const matchesDocumentType = (template, documentType) => {
  const terms = documentTemplateTerms[documentType] || [];
  if (!terms.length) return false;
  const text = templateText(template);
  return terms.some((term) => text.includes(term));
};

const findDefaultTemplate = ({ templates = [], documentType }) => {
  const activeTemplates = (templates || []).filter((template) => (
    template?.enabled !== false &&
    !crmTemplateCategories.has(String(template?.category || "").trim())
  ));
  return (
    activeTemplates.find((template) => isDefaultTemplate(template) && matchesDocumentType(template, documentType)) ||
    activeTemplates.find((template) => matchesDocumentType(template, documentType)) ||
    null
  );
};

const initialValue = ({ documentType, documentId, defaultRecipient, templates = [] }) => {
  const labels = {
    presentation: "Presentation",
    estimate: "Estimate",
    agreement: "Agreement",
  };
  const label = labels[documentType] || "document";
  const template = findDefaultTemplate({ templates, documentType });
  return {
    to: defaultRecipient ? [defaultRecipient] : [],
    cc: [],
    bcc: [],
    subject: `${label} #${documentId} from Elite Core`,
    body: template?.bodyHtml || `<p>Hi,</p><p>Your ${label.toLowerCase()} is ready for review.</p><p>Thanks!</p>`,
    templateId: template?.id || "",
    layout: "elite",
    quickActions: defaultQuickActions,
  };
};

export default function DocumentEmailDialog({
  open,
  onClose,
  onSend,
  sending,
  documentType,
  documentId,
  customerEmail,
  assignedTo = [],
  users = [],
  currentUserId,
  templates = [],
  resend = false,
}) {
  const currentUser = users.find((user) => Number(user.id) === Number(currentUserId));
  const assignedContacts = useMemo(
    () => assignedTo
      .map((assigned) => {
        const user = users.find((entry) => Number(entry.id) === Number(assigned?.id));
        const email = assigned?.email || user?.email;
        const name = assigned?.name || [user?.first_name, user?.last_name].filter(Boolean).join(" ");
        return {
          contact: asContact(email, name),
          role: String(assigned?.role || "").toLowerCase(),
        };
      })
      .filter((entry) => entry.contact),
    [assignedTo, users]
  );
  const me = useMemo(
    () => asContact(
      currentUser?.email,
      `${[currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(" ") || currentUser?.email || ""} (You)`
    ),
    [currentUser?.email, currentUser?.first_name, currentUser?.last_name]
  );
  const customer = useMemo(
    () => asContact(customerEmail, customerEmail),
    [customerEmail]
  );
  const contacts = useMemo(() => {
    const map = new Map();
    [customer, me, ...assignedContacts.map((entry) => entry.contact)]
      .filter(Boolean)
      .forEach((contact) => map.set(contact.id, contact));
    return [...map.values()];
  }, [assignedContacts, customer, me]);
  const [value, setValue] = useState(() => initialValue({
    documentType,
    documentId,
    defaultRecipient: customer,
    templates,
  }));
  const initKeyRef = useRef("");

  useEffect(() => {
    if (!open) return;
    const initKey = `${documentType || ""}:${documentId || ""}:${customer?.id || ""}`;
    const nextInitialValue = initialValue({
      documentType,
      documentId,
      defaultRecipient: customer,
      templates,
    });
    const fallbackInitialValue = initialValue({
      documentType,
      documentId,
      defaultRecipient: customer,
      templates: [],
    });

    setValue((current) => {
      if (initKeyRef.current !== initKey) {
        initKeyRef.current = initKey;
        return nextInitialValue;
      }

      if (
        nextInitialValue.templateId &&
        !current.templateId &&
        current.subject === fallbackInitialValue.subject &&
        current.body === fallbackInitialValue.body
      ) {
        return {
          ...current,
          templateId: nextInitialValue.templateId,
          subject: nextInitialValue.subject,
          body: nextInitialValue.body,
          layout: current.layout || "elite",
        };
      }

      return current;
    });
  }, [customer, documentId, documentType, open, templates]);

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {resend ? "Resend" : "Send"} {documentType === "agreement" ? "Agreement" : documentType === "estimate" ? "Estimate" : "Presentation"}
      </DialogTitle>
      <DialogContent dividers>
        <EmailComposer
          contacts={contacts}
          templates={templates}
          value={value}
          onChange={setValue}
          showAttachments={false}
          docId={documentId}
          quickActionRecipients={{
            me: me ? [me] : [],
            orderReps: assignedContacts.filter((entry) => entry.role === "order-rep").map((entry) => entry.contact),
            clientReps: assignedContacts.filter((entry) => entry.role === "client-rep").map((entry) => entry.contact),
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>Cancel</Button>
        <Button variant="contained" onClick={() => onSend(value)} disabled={sending}>
          {sending
            ? "Sending..."
            : `${resend ? "Resend" : "Send"} ${documentType === "agreement" ? "Agreement" : documentType === "estimate" ? "Estimate" : "Presentation"}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
